/**
 * Motor "palpitar" — prever antes; o resultado aparece dias depois.
 *
 * E o unico motor com estado que atravessa sessoes. Por isso e o ultimo da
 * ordem de implementacao: a crianca palpita hoje, o palpite vai para o caderno,
 * e so daqui a `dias_para_resultado` dias a fazenda mostra o que aconteceu.
 *
 * D6 e secao 10, regra 4: NUNCA se diz que o palpite estava certo ou errado.
 * Nem para a crianca, nem no relatorio dos pais. O que se mostra e o que
 * aconteceu. Palpitar e uma forma de olhar, nao uma aposta que se ganha.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

const CORES = [0xffb74d, 0x4fc3f7, 0xaed581];

export class PalpitarEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.opcoes = [];
    this.grupo = null;
    this.palpite = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    if (payload.opcoes > 3) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" pede ${payload.opcoes} opcoes. D8 permite no maximo 3.`
      );
    }

    this.quantidade = payload.opcoes;
    this.diasParaResultado = payload.dias_para_resultado;
    this.pergunta = tarefa.conteudo?.pergunta;

    const opcoes = tarefa.conteudo?.opcoes;
    if (!this.pergunta || !Array.isArray(opcoes) || opcoes.length < this.quantidade) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" precisa de "conteudo.pergunta" e de ` +
          `"conteudo.opcoes" com pelo menos ${this.quantidade} itens.`
      );
    }
    this.dados = opcoes.slice(0, this.quantidade);

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `pergunta "${this.pergunta}", resultado em ${this.diasParaResultado} dia(s).`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-palpitar';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    const largura = (this.quantidade - 1) * 2.6;

    this.dados.forEach((dado, i) => {
      const opcao = new THREE.Mesh(
        new THREE.BoxGeometry(1.7, 1.7, 0.3),
        new THREE.MeshStandardMaterial({ color: CORES[i], roughness: 0.9, flatShading: true })
      );
      opcao.position.set(base.x - largura / 2 + i * 2.6, base.y + 1.1, base.z + 2.2);
      opcao.userData.dado = dado;

      this.grupo.add(opcao);
      this.opcoes.push(opcao);
      input.registrar(opcao, () => this.aoPalpitar(opcao));
    });

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.opcoes[0], `opcao da tarefa ${this.tarefa.id}`);
  }

  aoPalpitar(opcao) {
    const { audio, instrucao, input } = this.ctx;

    // Qualquer palpite serve. Nao existe palpite errado (D6).
    this.palpite = opcao.userData.dado.nome ?? String(opcao.userData.dado);
    audio.tocarSucesso();

    for (const o of this.opcoes) input.desregistrar(o);
    instrucao.mostrarTexto('Anotei seu palpite no caderno. Vamos ver o que acontece!');
  }

  concluiu() {
    return this.palpite !== null;
  }

  atualizar(dt) {
    for (const o of this.opcoes) {
      if (!this.palpite) o.rotation.y = Math.sin(performance.now() / 700 + o.position.x) * 0.2;
    }
  }

  async concluir() {
    const evento = await super.concluir({ concluiu: this.concluiu() });
    // O efeito caderno.anotar usa estes campos.
    return {
      ...evento,
      pergunta: this.pergunta,
      palpite: this.palpite,
      dias_para_resultado: this.diasParaResultado,
    };
  }

  descartar() {
    const { input, sceneManager } = this.ctx;
    for (const o of this.opcoes) input.desregistrar(o);
    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
    }
    this.opcoes = [];
    this.grupo = null;
    this.palpite = null;
    super.descartar();
  }
}

/**
 * Resolve os palpites cujo prazo venceu. Roda na carga da sessao.
 * Nao compara palpite com resultado: so marca que ja da para ver o que houve.
 */
export function resolverPalpitesVencidos(estado) {
  let resolvidos = 0;
  for (const anotacao of estado.caderno) {
    if (anotacao.resolvido) continue;
    const prazo = anotacao.dia + (anotacao.dias_para_resultado ?? 3);
    if (estado.dia >= prazo) {
      anotacao.resolvido = true;
      resolvidos += 1;
    }
  }
  if (resolvidos > 0) {
    console.info(`FAZENDA: ${resolvidos} palpite(s) do caderno ja podem ser vistos.`);
  }
  return resolvidos;
}
